import { useMemo } from 'react'
import { take } from 'lodash'

import { useQuery } from '@tanstack/react-query'

import { useDebouncedValue } from '@collaborate-health/uikit'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { UsersQueryKeys } from '../queries'

const DEBOUNCE_TIME_MS = 500
const MAX_USERS_COUNT = 50

const EMPTY_SET: number[] = []

export interface UseUsersProps {
  contentId?: number
  search?: string
  roles?: number[]
  minSearchLength?: number
}

const useUsers = ({ contentId, search, roles = EMPTY_SET, minSearchLength = 3 }: UseUsersProps) => {
  const { mainApi: api } = useApi()
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_TIME_MS)

  const query = useQuery({
    queryKey: [UsersQueryKeys.Users, contentId, debouncedSearch, roles],
    queryFn: () => api.users.getAsync({ search: debouncedSearch, contentId, roles }),
    enabled: debouncedSearch?.length! >= minSearchLength,
    keepPreviousData: true,
  })

  return useMemo(
    () => ({ loading: query.isLoading, users: take(query.data ?? [], MAX_USERS_COUNT) }),
    [query.isLoading, query.data]
  )
}

export default useUsers

